// src/pages/dashboard.jsx
import React, { useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../contexts/AuthContext'
import { useSidebar } from '../contexts/SidebarContext'
import Button from '../components/Shared/Button'

import {
  SparklesIcon,
  Squares2X2Icon,
  ScaleIcon,
  CalculatorIcon,
  ClockIcon,
  PhotoIcon,
  TagIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

export default function Dashboard() {
  const navigate = useNavigate()
  const { openLibrary } = useSidebar()
  const { auth } = useContext(AuthContext)

  const firstName = useMemo(() => {
    const raw = auth?.user?.first_name || auth?.user?.name || ''
    const cleaned = String(raw || '').trim()
    return cleaned ? cleaned.split(' ')[0] : ''
  }, [auth])

  const handleCreateRecipe = () => {
    // Canonical creation entry point = Library (your modal flow)
    openLibrary()
  }

  const handleExploreTemplates = () => {
    navigate('/templates')
  }

  const handleGoCommunity = () => {
    // Community / feedback hub (add route later if needed)
    navigate('/community')
  }

  return (
    <div className="px-3 sm:px-4 py-6 sm:py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* 1) HERO + CTAs */}
        <section className="rounded-2xl border border-light-gray bg-soft-yellow/5 overflow-hidden shadow-sm">
          <div className="px-4 sm:px-6 py-6 sm:py-8 bg-white">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs sm:text-sm font-semibold text-slate-gray">
                  {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
                </div>

                <h1 className="mt-1 font-primary text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-charcoal">
                  Build recipes that feel like craft.
                </h1>

                <p className="mt-2 text-sm sm:text-base text-slate-gray max-w-2xl">
                  Ruoth turns your ideas into a clean, reusable system with beautiful cards, consistent structure,
                  and tools that help you scale and cost with confidence.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                  <Button
                    type="button"
                    onClick={handleCreateRecipe}
                    variant="primary"
                    className="w-full sm:w-auto justify-center gap-2"
                  >
                    Create a recipe
                    <ArrowRightIcon className="w-4 h-4" />
                  </Button>

                  <Button
                    type="button"
                    onClick={handleExploreTemplates}
                    variant="secondary"
                    className="w-full sm:w-auto justify-center gap-2"
                  >
                    Explore templates
                    <Squares2X2Icon className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="hidden md:flex items-center justify-center rounded-2xl border border-light-gray bg-soft-yellow/10 px-4 py-3">
                <div className="flex items-center gap-2 text-charcoal">
                  <SparklesIcon className="w-5 h-5" />
                  <div className="text-sm font-semibold">Premium by design</div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-light-gray">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  title: 'Human input → structured output',
                  desc: 'Paste ingredients naturally. Keep your recipes consistent.',
                },
                {
                  title: 'Scale without breaking the maths',
                  desc: 'Adjust servings and keep ratios clean.',
                },
                {
                  title: 'Cost with confidence',
                  desc: 'Turn ingredients into reliable costing workflows.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-light-gray bg-white px-4 py-3"
                >
                  <div className="text-sm font-semibold text-charcoal">{item.title}</div>
                  <div className="mt-1 text-xs text-slate-gray">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ✅ COMMUNITY: speak directly to the audience */}
        <section className="rounded-2xl border border-[#eadfd8] bg-white shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-5 border-b border-[#eadfd8]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-[#5a3e36]">
                  Welcome to the Ruoth Kitchen
                </h2>
                <p className="mt-1 text-sm text-[#7a6f66] max-w-3xl">
                  This was built for chefs, bakers, creators, and serious home cooks who care about craft, consistency,
                  and doing it properly. You’re not just using a tool, you’re joining a community that’s shaping what
                  Ruoth becomes.
                </p>
              </div>

              <div className="hidden sm:flex items-center gap-2 rounded-2xl border border-[#eadfd8] bg-[#fbf7f4] px-4 py-3">
                <UserGroupIcon className="w-5 h-5 text-[#5a3e36]" />
                <div className="text-sm font-semibold text-[#5a3e36]">You belong here</div>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[#eadfd8] bg-[#fbf7f4] p-4">
                <div className="text-sm font-semibold text-[#5a3e36]">Made for your day-to-day</div>
                <ul className="mt-3 space-y-2 text-sm text-[#5a3e36]">
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">Chefs & kitchen teams</span>: consistency, scaling, costing</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">Bakers & pastry</span>: precision, ratios, repeatability</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">Creators</span>: premium recipes you can deliver & sell</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">Food businesses</span>: menus, collections, planning</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">R&amp;D chefs &amp; test kitchens</span>: iterate fast, track versions, lock in winners</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span><span className="font-semibold">Serious home cooks</span>: build a system, not a mess</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-[#eadfd8] bg-white p-4">
                <div className="flex items-center gap-2 text-[#5a3e36]">
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  <div className="text-sm font-semibold">Your voice shapes the product</div>
                </div>

                <p className="mt-2 text-sm text-[#7a6f66]">
                  Tell us what you want to see next; dashboard insights, exports, collaboration, pro workflows.
                  We’re building Ruoth with you, not just for you.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={handleGoCommunity}
                    variant="primary"
                    className="w-full sm:w-auto justify-center gap-2"
                  >
                    Enter the community
                    <ArrowRightIcon className="w-4 h-4" />
                  </Button>

                  <Button
                    type="button"
                    onClick={handleCreateRecipe}
                    variant="secondary"
                    className="w-full sm:w-auto justify-center gap-2"
                  >
                    Start your next recipe
                    <ArrowRightIcon className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-3 text-xs text-[#7a6f66]">
                  You’re early and that matters. Your feedback directly influences what ships next.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2) LIVE NOW (tiles) */}
        <section className="rounded-2xl border border-[#eadfd8] bg-white shadow-sm">
          <div className="px-4 sm:px-6 py-5 border-b border-[#eadfd8]">
            <h2 className="text-lg sm:text-xl font-semibold text-[#5a3e36]">Live now</h2>
            <p className="mt-1 text-sm text-[#7a6f66]">
              These aren’t “coming soon” this is what you can use today.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { icon: Squares2X2Icon, title: 'Beautiful templates', sub: 'Cards that feel like craft' },
                { icon: ScaleIcon, title: 'Scaling tools', sub: 'Servings, ratios, consistency' },
                { icon: CalculatorIcon, title: 'Costing tools', sub: 'Estimate & price with clarity' },
                { icon: ClockIcon, title: 'Version history', sub: 'Iterate without losing work' },
                { icon: PhotoIcon, title: 'Media support', sub: 'Scan images + import from PDFs' },
                { icon: TagIcon, title: 'Tags & organisation', sub: 'Find what you need fast' },
                { icon: ShieldCheckIcon, title: 'Entitlements', sub: 'Access based on purchases' },
                { icon: SparklesIcon, title: 'Premium feel', sub: 'Fast, clean, mobile-first' },
              ].map((t) => {
                const Icon = t.icon
                return (
                  <div
                    key={t.title}
                    className="rounded-xl border border-[#eadfd8] bg-[#fbf7f4] px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-[#5a3e36]" />
                      <div className="text-sm font-semibold text-[#5a3e36]">{t.title}</div>
                    </div>
                    <div className="mt-1 text-xs text-[#7a6f66]">{t.sub}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* 3) INGREDIENT INTELLIGENCE (mini demo + benefit) */}
        <section className="rounded-2xl border border-[#eadfd8] bg-white shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-5 border-b border-[#eadfd8]">
            <h2 className="text-lg sm:text-xl font-semibold text-[#5a3e36]">
              Ingredient Intelligence (built in)
            </h2>
            <p className="mt-1 text-sm text-[#7a6f66]">
              Paste ingredients like a human. Ruoth understands them like a system.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-5">
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Mini demo */}
              <div className="rounded-2xl border border-[#eadfd8] bg-[#fbf7f4] p-4">
                <div className="text-xs font-semibold text-[#7a6f66]">Mini demo</div>

                <div className="mt-3 rounded-xl border border-[#eadfd8] bg-white p-3">
                  <div className="text-xs font-semibold text-[#5a3e36]">Input</div>
                  <div className="mt-2 space-y-2 text-sm text-[#5a3e36]">
                    <div className="rounded-lg bg-[#fbf7f4] px-3 py-2 border border-[#eadfd8]">
                      2 tbsp chopped olives
                    </div>
                    <div className="rounded-lg bg-[#fbf7f4] px-3 py-2 border border-[#eadfd8]">
                      1 can chickpeas (drained)
                    </div>
                    <div className="rounded-lg bg-[#fbf7f4] px-3 py-2 border border-[#eadfd8]">
                      salt to taste
                    </div>
                  </div>
                </div>

                <div className="mt-3 rounded-xl border border-[#eadfd8] bg-white p-3">
                  <div className="text-xs font-semibold text-[#5a3e36]">Structured</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    {[
                      'qty', 'unit', 'ingredient', 'prep',
                      'notes', 'optional', 'warnings', 'confidence',
                    ].map((k) => (
                      <div
                        key={k}
                        className="rounded-lg bg-[#fbf7f4] px-2 py-2 border border-[#eadfd8] text-[#5a3e36]"
                      >
                        <span className="font-semibold">{k}</span>
                        <span className="text-[#7a6f66]"> —</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 text-xs text-[#7a6f66]">
                    This structure is what unlocks reliable scaling, costing, nutrition, and inventory features.
                  </div>
                </div>
              </div>

              {/* Benefits copy */}
              <div className="rounded-2xl border border-[#eadfd8] bg-white p-4">
                <div className="text-sm font-semibold text-[#5a3e36]">Why it matters</div>

                <ul className="mt-3 space-y-3 text-sm text-[#5a3e36]">
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span>
                      <span className="font-semibold">No silent wrongness:</span> uncertainty becomes a review flow,
                      not broken recipes.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span>
                      <span className="font-semibold">Fix once, keep forever:</span> manual overrides stay respected.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-block w-2 h-2 rounded-full bg-[#c7a795]" />
                    <span>
                      <span className="font-semibold">Built for real kitchens:</span> slang, notes, and messy input
                      are handled thoughtfully.
                    </span>
                  </li>
                </ul>

                <div className="mt-4 rounded-xl border border-[#eadfd8] bg-[#fbf7f4] p-4">
                  <div className="text-xs font-semibold text-[#7a6f66]">The point</div>
                  <div className="mt-1 text-sm font-semibold text-[#5a3e36]">
                    Your recipes become a system and not a pile of text.
                  </div>
                  <div className="mt-2 text-xs text-[#7a6f66]">
                    This is the foundation for the “Creator Club” experience: structure, confidence, and speed.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4) ROADMAP (Now / Next / Later) */}
        <section className="rounded-2xl border border-light-gray bg-white shadow-sm">
          <div className="px-4 sm:px-6 py-5 border-b border-light-gray">
            <h2 className="text-lg sm:text-xl font-semibold text-charcoal">Roadmap</h2>
            <p className="mt-1 text-sm text-slate-gray">
              A clear path designed around real culinary workflows.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-5">
            <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
              {[
                {
                  label: 'Now',
                  title: 'Full creator workflow (live today)',
                  desc: 'Templates, editor, scaling, costing, nutrition, ingredient intelligence, menus, collections, and meal planning all working in one flow.',
                },
                {
                  label: 'Next',
                  title: 'Export + collaboration',
                  desc: 'PDF export and multi-user collaboration so creators can share, deliver, and work together professionally.',
                },
                {
                  label: 'Later',
                  title: 'R&D Advanced (21–31)',
                  desc: 'Higher-end experimentation and next-level intelligence layers once the export + collab foundation is shipped.',
                },
              ].map((r) => (
                <div
                  key={r.label}
                  className="rounded-2xl border border-light-gray bg-soft-yellow/5 p-4"
                >
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white text-charcoal border border-light-gray">
                    {r.label}
                  </div>
                  <div className="mt-3 text-sm font-semibold text-charcoal">{r.title}</div>
                  <div className="mt-1 text-xs text-slate-gray">{r.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5) TEMPLATES SPOTLIGHT (featured designs) */}
        <section className="rounded-2xl border border-[#eadfd8] bg-white shadow-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-5 border-b border-[#eadfd8] flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-[#5a3e36]">Templates spotlight</h2>
              <p className="mt-1 text-sm text-[#7a6f66]">
                A living collection designed to look like work you’d serve.
              </p>
            </div>

            <button
              type="button"
              onClick={handleExploreTemplates}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#eadfd8] bg-white text-[#5a3e36] font-semibold text-sm hover:bg-[#f5eee9] active:scale-[0.99] transition"
            >
              View all
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 sm:px-6 py-5">
            {/* Frontend-only spotlight (no API dependency). Replace with real featured templates later. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { title: 'Minimal Classic', sub: 'Clean, modern, effortless' },
                { title: 'Premium Editorial', sub: 'For chefs & creators' },
                { title: 'Vintage Heirloom', sub: 'Warm, timeless, elegant' },
              ].map((c) => (
                <div
                  key={c.title}
                  className="rounded-2xl border border-[#eadfd8] bg-[#fbf7f4] p-4"
                >
                  <div className="text-sm font-semibold text-[#5a3e36]">{c.title}</div>
                  <div className="mt-1 text-xs text-[#7a6f66]">{c.sub}</div>

                  <div className="mt-3 rounded-xl border border-[#eadfd8] bg-white p-3">
                    <div className="h-24 rounded-lg bg-[#f1e9e4]" />
                    <div className="mt-2 h-3 w-2/3 rounded bg-[#f1e9e4]" />
                    <div className="mt-2 h-3 w-1/2 rounded bg-[#f1e9e4]" />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-[#eadfd8] bg-white p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="text-sm text-[#5a3e36]">
                <span className="font-semibold">New designs added regularly.</span>{' '}
                Build a library that looks as good as it works.
              </div>
              <button
                type="button"
                onClick={handleCreateRecipe}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#5a3e36] text-white px-5 py-3 rounded-xl font-semibold text-sm hover:bg-[#432e29] active:scale-[0.99] transition"
              >
                Create a recipe
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}